// VORSØ native bundles: multipack toggle + sets (replaces Kaching Bundles).
const money = (cents) => {
  const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  const whole = Math.round(cents) % 100 === 0;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  } catch (e) {
    return '$' + (cents / 100).toFixed(whole ? 0 : 2);
  }
};

const rate = () => parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1;

async function dispatchCartUpdate(source, lines, cart) {
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const d = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: d.promise })
    );
    d.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'vs-bundle' },
    });
  } catch (e) {
    /* theme events unavailable: drawer is still updated directly */
  }
}

class VsBundle extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.mode = this.dataset.mode;
    this.pills = this.dataset.layout === 'pills';
    // nearest product form (works on the product page and inside quick-add modals)
    let scope = this.parentElement;
    while (scope && !scope.querySelector('form[data-type="add-to-cart-form"]')) scope = scope.parentElement;
    this.scope = scope;
    this.form = scope ? scope.querySelector('form[data-type="add-to-cart-form"]') : null;

    if (rate() !== 1) {
      this.querySelectorAll('[data-shop-cents]').forEach((el) => {
        el.textContent = money(Number(el.dataset.shopCents) * rate());
      });
    }

    this.addEventListener('change', this.onChange);
    this.onSubmit = this.onSubmit.bind(this);
    window.addEventListener('submit', this.onSubmit, true);

    if (this.mode === 'pack') {
      const checked = this.querySelector('input[type=radio]:checked');
      this.qty = checked ? Number(checked.value) : 1;
      this.syncForm();
      if (this.pills) {
        this.inDialog = !!this.closest('dialog');
        this.root = this.closest('dialog') || this.closest('.shopify-section') || document.body;
        this.syncPrice();
        // the theme swaps in fresh price markup after a size change; put the pack price back
        // runs before paint, so the single price never flashes; no-op when already correct
        this.mo = new MutationObserver(() => this.syncPrice());
        this.mo.observe(this.root, { childList: true, subtree: true });
        if (!this.inDialog) {
          document.querySelectorAll('sticky-add-to-cart').forEach((el) => {
            if (!this.root.contains(el)) this.mo.observe(el, { childList: true, subtree: true });
          });
        }
      }
    } else {
      try {
        this.data = JSON.parse(this.querySelector('script[data-vs-bundle-data]').textContent);
      } catch (e) {
        this.data = [];
      }
      this.querySelectorAll('.vs-bundle__product').forEach((el) => this.resolveVariant(el));
      // sent here by the cart guard (a set was added without sizes)
      if (/[?&]pick_sizes=1/.test(location.search)) {
        this.showError('Choose a size for each piece, then add the set.');
        try {
          const u = new URL(location.href);
          u.searchParams.delete('pick_sizes');
          history.replaceState(history.state, '', u.pathname + u.search + u.hash);
        } catch (e) {}
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.onSubmit, true);
    if (this.mo) this.mo.disconnect();
    this._ready = false;
  }

  onChange = (e) => {
    const t = e.target;
    if (this.mode === 'pack' && t.matches('input[type=radio]')) {
      const prev = this.qty;
      this.qty = Number(t.value);
      this.querySelectorAll('[data-qty]').forEach((b) =>
        b.classList.toggle('is-selected', Number(b.dataset.qty) === this.qty)
      );
      if (this.pills) {
        // drives the theme's sliding-pill animation, same as the size picker
        this.querySelectorAll('input[type=radio]').forEach((i) => {
          i.dataset.previousChecked = String(Number(i.value) === prev);
          i.dataset.currentChecked = String(i.checked);
        });
        this.syncPrice();
      }
      this.syncForm();
    } else if (this.mode === 'set' && t.matches('select')) {
      this.resolveVariant(t.closest('.vs-bundle__product'));
      this.showError('');
    }
  };

  // Multipack: quantity + hidden line properties ride along with the theme's own add-to-cart.
  setHidden(name, value) {
    if (!this.form) return;
    let input = [...this.form.querySelectorAll('input[data-vs-bundle]')].find((i) => i.name === name);
    if (value == null) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.vsBundle = '';
      this.form.appendChild(input);
    }
    input.value = value;
  }

  syncForm() {
    const pack = this.qty > 1;
    this.setHidden('quantity', pack ? String(this.qty) : null);
    this.setHidden('properties[_bundle]', pack ? this.dataset.title : null);
    this.setHidden('properties[_bundle_type]', pack ? 'pack' : null);
  }

  // Pills: the product price (and sticky bar price) shows the pack total while the pack is selected.
  syncPrice() {
    if (!this.root) return;
    const pack = this.qty > 1;
    const r = rate();
    const packCents = Number(this.dataset.packCents) * r;
    const fullCents = Number(this.dataset.unitCents) * Number(this.dataset.packQty) * r;
    let html = '';
    if (pack) {
      const tpl = this.querySelector('template[data-vs-pack-price]');
      html =
        r === 1 && tpl
          ? tpl.innerHTML
          : `<span class="price-item__group price"><span class="visually-hidden">${this.dataset.saleLabel}&nbsp;</span><bdi>${money(packCents)}</bdi></span> <span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">${this.dataset.regularLabel}&nbsp;</span><bdi>${money(fullCents)}</bdi></span>`;
    }
    const swap = (el, markup) => {
      if (!el.hasAttribute('data-vs-orig')) el.setAttribute('data-vs-orig', el.innerHTML);
      const next = pack ? markup : el.getAttribute('data-vs-orig');
      if (el._vsSet === next && el._vsGot === el.innerHTML) return; // our own write: stop (no observer loop)
      if (el.innerHTML !== next) el.innerHTML = next;
      el._vsSet = next;
      el._vsGot = el.innerHTML;
    };
    const pid = this.dataset.productId;
    this.root.querySelectorAll(`product-price[data-product-id="${pid}"] [ref="priceContainer"]`).forEach((el) => {
      if (this.inDialog || !el.closest('dialog')) swap(el, html);
    });
    if (!this.inDialog) {
      document
        .querySelectorAll(`sticky-add-to-cart[data-product-id="${pid}"] [ref="priceContainer"]`)
        .forEach((el) => swap(el, `<span class="price"><bdi>${money(packCents)}</bdi></span>`));
    }
  }

  // Set: map each card's selects to a variant of that component.
  resolveVariant(el) {
    if (!el) return;
    const comp = this.data[Number(el.dataset.index)];
    if (!comp) return;
    const picked = [...el.querySelectorAll('select')].map((s) => s.value);
    const v = picked.length
      ? comp.variants.find((x) => picked.every((val, i) => x.options[i] === val))
      : comp.variants[0];
    el.dataset.variantId = v ? v.id : '';
    el.classList.toggle('is-unavailable', !v || !v.available);
    if (v) el.querySelectorAll('a.vs-bundle__plink').forEach((a) => (a.href = `${comp.url}?variant=${v.id}`));
  }

  showError(msg) {
    const p = this.querySelector('.vs-bundle__error');
    if (!p) return;
    p.textContent = msg;
    p.hidden = !msg;
  }

  onSubmit(e) {
    if (!this.form || e.target !== this.form) return;
    if (this.mode === 'pack') {
      this.syncForm(); // theme may have re-rendered the form after a variant change
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    this.addSet();
  }

  async addSet() {
    if (this.busy) return;
    const cards = [...this.querySelectorAll('.vs-bundle__product')];
    if (cards.some((c) => !c.dataset.variantId || c.classList.contains('is-unavailable'))) {
      this.showError('One of the selected sizes is sold out. Choose another size to add the set.');
      return;
    }
    this.busy = true;
    const btn = this.form.querySelector('[type=submit]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    const groupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const items = cards.map((c, i) => ({
      id: Number(c.dataset.variantId),
      quantity: 1,
      properties: { _bundle: this.dataset.title, _bundle_type: 'set', _bundle_id: groupId, _bundle_part: String(i + 1) },
    }));
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.slice().reverse() }), // cart lists newest first; keep set order
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add the set.');
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.showError('');
      if (window.vsCart) window.vsCart.setCart(cart, { added: true });
      dispatchCartUpdate(
        this,
        items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
        cart
      );
      const container = this.form.closest('product-form-component');
      if (container && container.refs && container.refs.addToCartButtonContainer) {
        container.refs.addToCartButtonContainer.animateAddToCart?.();
      }
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.busy = false;
      if (btn) btn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('vs-bundle')) customElements.define('vs-bundle', VsBundle);
